import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../redux/reducers/index";
import {
  setMarcaBlancaValidated,
  setMarcaBlancaClient,
} from "../../redux/actions/marcaBlancaActions";
import { Card } from "../../theme/theme";
import ModalButton from "../ModalButton";
import useDocumentTypes from "../../hooks/useDocumentTypes";
import useMarcaBlancaAuth, {
  MarcaBlancaAuthResponse,
} from "../../hooks/useMarcaBlancaAuth";
import useMarcaBlancaVerify from "../../hooks/useMarcaBlancaVerify";
import MarcaBlancaRequirementsModal from "../MarcaBlancaRequirementsModal";
import MarcaBlancaGenericErrorModal from "../MarcaBlancaGenericErrorModal";
import { redirectTo } from "../../utils/redirect";
import config from "../../config/enviroment";
import { getRequirementModalConfig } from "../../utils/marcaBlancaValidation";
import {
  isValidDocumentNumber,
  normalizeDocumentNumber,
} from "../../utils/documentValidation";
import { language } from "../../utils/language";
import {
  StyledPopup,
  HeadCard,
  IconWrap,
  Title,
  Intro,
  Field,
  FieldError,
  FormGroup,
  BottomCard,
  Footer,
  FooterLink,
} from "./styles";
import { WarningCircleIcon, XIcon } from "@phosphor-icons/react";

type MarcaBlancaModalProps = {
  hashAuthResponse?: MarcaBlancaAuthResponse | null;
};

const MarcaBlancaModal: React.FC<MarcaBlancaModalProps> = ({
  hashAuthResponse = null,
}) => {
  const homeState: HomeStateType = useSelector(
    (state: RootState) => state.home
  );
  const t = language[homeState.default_language];
  const dispatch = useDispatch();
  const marcaBlanca = useSelector((state: RootState) => state.marcaBlanca);
  const { documentTypes } = useDocumentTypes();
  const { authenticate, loading } = useMarcaBlancaAuth();
  const { verify } = useMarcaBlancaVerify();

  const [open, setOpen] = useState(!hashAuthResponse);
  const [epaycoId, setEpaycoId] = useState("");
  const [typeDocument, setTypeDocument] = useState("");
  const [numberDocument, setNumberDocument] = useState("");
  const [showErrors, setShowErrors] = useState(false);
  const [authResponse, setAuthResponse] =
    useState<MarcaBlancaAuthResponse | null>(hashAuthResponse);
  const [showRequirements, setShowRequirements] = useState(
    Boolean(hashAuthResponse)
  );

  const idTrimmed = epaycoId.trim();
  const idFilled = idTrimmed !== "";
  const idValid = /^\d{1,10}$/.test(idTrimmed);

  const typeValid = typeDocument !== "";

  const selectedType = documentTypes.find((d) => d.code === typeDocument);
  const isNIT =
    typeDocument === "NIT" || selectedType?.digitVerification === true;
  const numberTrimmed = normalizeDocumentNumber(numberDocument);
  const numberFilled = numberTrimmed !== "";
  const numberValid = isValidDocumentNumber(selectedType, numberTrimmed);
  const numberError = !numberFilled
    ? t["documentNumberRequired"]
    : isNIT
    ? t["documentNit"]
    : t["documentNumber"];

  const formValid = idValid && typeValid && numberValid;

  // Los errores de formato aparecen en vivo (apenas hay contenido inválido);
  // los de "requerido" también salen al presionar Validar.
  const showIdError = (showErrors || idFilled) && !idValid;
  const showTypeError = showErrors && !typeValid;
  const showNumberError = (showErrors || numberFilled) && !numberValid;

  // Si el guardia bloquea un acceso (p. ej. tras manipular el DOM), reabre el
  // modal principal para forzar de nuevo la validación.
  useEffect(() => {
    if (marcaBlanca.lastBlockedAt && !hashAuthResponse) {
      setShowRequirements(false);
      setOpen(true);
    }
  }, [marcaBlanca.lastBlockedAt]);

  const goHome = () => redirectTo(config.epaycoHomeUrl);

  // Mismo comportamiento que AnddressInfo: solo dígitos/letras, y "-" solo para NIT.
  const handleDocumentInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key.length > 1) return; // permite Backspace, flechas, Tab, etc.
    if (isNIT && e.key === "-") return;
    const isDigit = /^[0-9]$/.test(e.key);
    const isLetter = /^[A-Za-z]$/.test(e.key);
    if (!isDigit && !isLetter) {
      e.preventDefault();
    }
  };

  const handleEnter = async () => {
    if (!formValid) {
      setShowErrors(true);
      return;
    }
    const result = await authenticate({
      epaycoId,
      typeDocument,
      numberDocument: numberTrimmed,
      documentTypeId: selectedType?.id ?? "",
    });

    // Si el cliente existe (haya pasado o no el 100%), persistimos la validación
    // en el backend con el segundo endpoint. Si el cliente no existe (sin data),
    // no se llama y el flujo queda igual que antes.
    if (result?.data) {
      const { ok } = await verify({
        clientId: epaycoId,
        documentNumber: numberTrimmed,
        documentTypeId: selectedType?.id ?? "",
      });

      // Solo si el backend confirma, guardamos los datos para el checkout.
      if (ok) {
        dispatch(setMarcaBlancaClient(result));
      }
    }

    if (result?.data?.status) {
      // Guarda las credenciales para re-verificar en los chokepoints.
      dispatch(
        setMarcaBlancaValidated({
          clientId: epaycoId,
          documentNumber: numberTrimmed,
          documentTypeId: selectedType?.id ?? "",
          documentTypeCode: typeDocument,
        })
      );
      setOpen(false);
    } else if (result) {
      // status === false => cerrar el principal y abrir el modal de requisitos
      setAuthResponse(result);
      setOpen(false);
      setShowRequirements(true);
    }
  };

  const handleAcknowledge = () => {
    // Limpiar el formulario y reabrir el modal principal
    setEpaycoId("");
    setTypeDocument("");
    setNumberDocument("");
    setShowErrors(false);
    setAuthResponse(null);
    setShowRequirements(false);
    setOpen(true);
  };

  const requirementModal = getRequirementModalConfig(authResponse, t, {
    epaycoRegisterUrl: config.epaycoRegisterUrl,
    epaycoHomeUrl: config.epaycoHomeUrl,
    epaycoValidateAccountUrl: config.epaycoValidateAccountUrl,
    daviviendaOpenAccountUrl: config.daviviendaOpenAccountUrl,
  });

  return (
    <>
    <StyledPopup
      open={open}
      modal
      closeOnDocumentClick={false}
      closeOnEscape={false}
      onClose={() => undefined}
    >
      <Card className="container">
        <HeadCard>
          <XIcon
            size={20}
            color="#999999"
            style={{ cursor: "pointer" }}
            onClick={goHome}
          />
        </HeadCard>

        <IconWrap>
          <WarningCircleIcon size={50} weight="regular" color="#ffd100" id="iconWarning" />
        </IconWrap>

        

        <FormGroup>
        <Intro>{t["marcaBlancaTitle"]}</Intro>
        <Intro>{t["marcaBlancaIntroOne"]}<br/>{t["marcaBlancaIntroTwo"]}<br/>{t["marcaBlancaIntroThree"]}</Intro>
        <Field $error={showIdError}>
          <input
            id="mb-epayco-id"
            type="text"
            inputMode="numeric"
            aria-label={t["marcaBlancaIdLabel"]}
            placeholder={t["marcaBlancaIdLabel"]}
            value={epaycoId}
            onChange={(e) => setEpaycoId(e.target.value.replace(/[^\d]/g, ""))}
          />
          {showIdError && (
            <FieldError>
              {idFilled
                ? t["marcaBlancaIdInvalid"]
                : t["marcaBlancaIdRequired"]}
            </FieldError>
          )}
        </Field>

        <Field $error={showTypeError}>
          <select
            id="mb-type-document"
            required
            aria-label={t["typeDocumentLabel"]}
            value={typeDocument}
            onChange={(e) => setTypeDocument(e.target.value)}
          >
            <option value="" disabled hidden>
              {t["typeDocumentLabel"]}
            </option>
            {documentTypes.map((dt) => (
              <option key={dt.id} value={dt.code}>
                {dt.name}
              </option>
            ))}
          </select>
          {showTypeError && (
            <FieldError>{t["documentTypeRequired"]}</FieldError>
          )}
        </Field>

        <Field $error={showNumberError}>
          <input
            id="mb-number-document"
            type="text"
            maxLength={25}
            aria-label={t["marcaBlancaNumberPlaceholder"]}
            placeholder={t["marcaBlancaNumberPlaceholder"]}
            value={numberDocument}
            onKeyDown={handleDocumentInput}
            onChange={(e) => setNumberDocument(e.target.value)}
          />
          {showNumberError && <FieldError>{numberError}</FieldError>}
        </Field>

        <BottomCard>
          <ModalButton
            color={homeState.color}
            secondary={false}
            disabled={loading || !formValid}
            onClick={handleEnter}
          >
            {t["marcaBlancaEnter"]}
          </ModalButton>
        </BottomCard>
        
        </FormGroup>
        <Footer>
          {t["marcaBlancaNoAccount"]}
          <FooterLink
            href={config.epaycoRegisterUrl}
            target="_blank"
            rel="noreferrer"
          >
            {t["marcaBlancaCreateLink"]}
          </FooterLink>
          {t["marcaBlancaNoAccountSuffix"]}
        </Footer>
      </Card>
    </StyledPopup>

    <MarcaBlancaRequirementsModal
      open={showRequirements && requirementModal.variant !== "generic"}
      color={homeState.color}
      title={requirementModal.title}
      intro={requirementModal.intro}
      requirements={requirementModal.requirements}
      actionLabel={requirementModal.actionLabel}
      onClose={handleAcknowledge}
      onAction={() =>
        requirementModal.actionUrl
          ? redirectTo(requirementModal.actionUrl)
          : handleAcknowledge()
      }
    />

    <MarcaBlancaGenericErrorModal
      open={showRequirements && requirementModal.variant === "generic"}
      color={homeState.color}
      title={requirementModal.title}
      detail={requirementModal.detail ?? ""}
      actionLabel={requirementModal.actionLabel}
      onClose={handleAcknowledge}
      onAction={() =>
        window.open(config.epaycoSupportTicketUrl, "_blank", "noopener,noreferrer")
      }
    />
    </>
  );
};

export default MarcaBlancaModal;
